import { GroupNotesSheet } from '../components/GroupNotesSheet';
import { Fragment, useEffect, useState } from 'react';
import { fmtLongDay, fmtShortDay, fmtTime } from '../lib/time';
import { alert, confirm, haptic, openExternal } from '../lib/telegram';
import { getLang, localeOf, translate, useT } from '../lib/i18n';
import { TagSheet, type Tag } from '../components/TagSheet';
import { byMonth, SessionHead, SessionRow as SessionLine } from '../components/SessionRow';
import { PaySwitchSheet } from '../components/PaySwitch';
import { EditPaymentSheet, PaymentHistory } from '../components/LessonPayment';
import { RowMenu } from '../components/RowMenu';
import { CoverageLine, costsLaterText, payCell, type Coverage, type CostsLater, type PayState } from '../components/Coverage';
import { CancelSheet } from '../components/CancelSheet';
import { rememberMethod, type PayMethod } from '../components/MethodPicker';
import { ProgressChart } from './Screens';
import { RatingSheet } from './Dashboard';
import { GrowArea } from '../components/GrowArea';
import { AboutChips, AboutSheet, AiNotes, api as profileApi, LevelCard, LevelSheet, LevelTrend, SessionsTab, SessionView, type Profile, type SessionRow } from './PlayerProfile';
import { Vis } from '../components/Vis';
import { LinkConfirmSheet, LinkPickSheet } from '../components/OfflineSheets';
import { api } from './client/api';
import { RatesSection } from './client/RatesSection';
import { BookForSheet } from '../components/BookForSheet';
import { MoveSheet } from '../components/MoveSheet';
import { ClientSport, StartingScoresCard, BaselineSheet } from './client/SportCards';
import { RegularCard } from './client/RegularCard';
import { PayerSection } from './client/PayerSection';
import { MatchList, CoachJournal } from './client/History';
import { TopUpSheet } from './client/TopUpSheet';
import { BotLanguageCard } from './client/BotLanguageCard';
export { setDetailToken } from './client/api';

interface Detail {
  client: {
    id: string; first_name: string; last_name: string | null; username: string | null;
    phone: string | null; bot_started: boolean; flagged: boolean; notes: string | null;
    /** Marked «Постоянный» by the coach (sql/037). */
    is_regular?: boolean;
    /** «Язык сообщений» (sql/041): the bot's language for this client; null — по умолчанию. */
    bot_language?: 'ru' | 'en' | null;
    nickname: string | null;
    /** Added by hand, no Telegram (sql/030). */
    offline?: boolean; added_manually_at?: string | null;
    health_consent?: 'yes' | 'no' | 'unanswered';
    working_on: string | null;
    pays_for_by?: string | null;
    half_hour_starts?: boolean;
    blocked?: boolean;
    created_at: string; credits: number;
  };
  sessions: {
    booking_id: string; status: string; payment_mode: 'credits' | 'cash';
    amount_due: number | null; amount_paid_at: string | null; credits_charged: number | null; series_id: string | null;
    /** Paid partly in credits (what they are worth), and cash received so far. */
    credit_value?: number | null; received?: number | null;
    starts_at: string; ends_at: string; location: string | null; title: string;
    kind?: string; people?: number;
    /** A future booking's «Оплата», from the server's walletState() (slice 2). */
    pay?: PayState | null; pay_due?: number | null;
  }[];
  /** «Запланировано · покрыто кредитами · нужна оплата» (slice 2). */
  coverage?: Coverage | null;
  packages: {
    id: string; credits_granted: number; balance: number; amount_paid: number;
    payment_status: string;
    purchased_at: string; expires_at: string | null; package_name: string | null;
  }[];
  totals: {
    taken: number; no_shows: number; cancelled: number; owed: number;
    first_session: string | null; last_session: string | null;
  };
  groups?: Tag[];
  rhythm: {
    sessions8w: number;
    frequency: 'twiceWeekly' | 'weekly' | 'fortnightly' | 'occasional' | 'none';
    level: number | null;
    progress: number | null;
  };
  lifetimeValue: number;
}

/** Two letters for the avatar: first and last word, or the first two letters of one. */
function initials(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const pick = words.length > 1 ? words[0][0] + words[words.length - 1][0] : words[0].slice(0, 2);
  return pick.toUpperCase();
}

/**
 * How long ago, for a date in the past. relativeTo only counts forward,
 * so it answered "passed" for every last session ever held.
 */
/**
 * The facts a coach has actually recorded, and only those: a level nobody
 * has set says nothing worth a line of its own.
 */
function ProfileFacts({ rhythm }: {
  rhythm: { frequency: string; level: number | null; progress: number | null };
}) {
  const t = useT();
  const parts: JSX.Element[] = [];

  if (rhythm.frequency !== 'none') {
    parts.push(
      <span key="freq" className={rhythm.frequency === 'twiceWeekly' || rhythm.frequency === 'weekly'
        ? 'clientcard__strong' : ''}>
        {t(`co.freq.${rhythm.frequency}` as 'co.freq.weekly')}
      </span>,
    );
  }
  if (rhythm.level !== null) {
    parts.push(<span key="level">{t('co.level', { n: rhythm.level.toFixed(1) })}</span>);
  }
  if (rhythm.progress !== null && Math.abs(rhythm.progress) >= 0.2) {
    const up = rhythm.progress > 0;
    parts.push(
      <span key="progress">
        <i className={`arrow arrow--${up ? 'up' : 'down'}`} aria-hidden="true" />
        {t(up ? 'co.progress.up' : 'co.progress.down', { n: Math.abs(rhythm.progress).toFixed(1) })}
      </span>,
    );
  }

  if (parts.length === 0) return null;
  return (
    <div className="profile__facts">
      {parts.map((part, i) => (
        <Fragment key={part.key}>{i > 0 && <i>·</i>}{part}</Fragment>
      ))}
    </div>
  );
}

function ago(iso: string) {
  const tr = (key: Parameters<typeof translate>[1], vars?: Record<string, string | number>) =>
    translate(getLang(), key, vars);
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return tr('co.cd.agoToday');
  if (days === 1) return tr('co.cd.agoYesterday');
  if (days < 14) return tr('co.cd.agoDays', { n: days });
  if (days < 60) return tr('co.cd.agoWeeks', { n: Math.round(days / 7) });
  return tr('co.cd.agoMonths', { n: Math.round(days / 30) });
}

export function ClientDetail({
  clientId, onBack, onSell, onOpenClient, backLabel, preview, onOpenLesson,
}: {
  clientId: string; onBack: () => void; onSell: (c: any) => void; onOpenClient?: (id: string, name?: string) => void;
  /** Where «Назад» goes, in words: «‹ Календарь», «‹ Клиенты», «‹ Назад» (lib/nav-stack.ts). */
  backLabel?: string;
  /** What the screen that opened the card already knew: shown while the rest loads. */
  preview?: { name?: string };
  /** A lesson opened from the card goes on the coach's history (CoachScreen); without it, as before. */
  onOpenLesson?: (bookingId: string) => void;
}) {
  const [d, setD] = useState<Detail | null>(null);
  const [notes, setNotes] = useState('');
  const [savedNotes, setSavedNotes] = useState('');
  const [nickname, setNickname] = useState('');
  const [savedNickname, setSavedNickname] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [workingOn, setWorkingOn] = useState('');
  const [savedWorkingOn, setSavedWorkingOn] = useState('');
  const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');
  const [cancelOne_, setCancelOne] = useState<any | null>(null);
  const [cancelAll, setCancelAll] = useState(false);
  const [moving, setMoving] = useState<any | null>(null);
  const [baseline, setBaseline] = useState(false);
  // «Заменить оплату» for one of this client's lessons.
  const [switchFor, setSwitchFor] = useState<string | null>(null);
  // «+ Кредит»: the amount and how it was paid, in one sheet (TopUpSheet).
  const [toppingUp, setToppingUp] = useState(false);
  // «История оплат», and «Изменить оплату» on a lesson paid ahead (components/LessonPayment.tsx).
  const [payHistory, setPayHistory] = useState(false);
  const [editPay, setEditPay] = useState<string | null>(null);
  // Bumped when the starting scores sheet closes, so the card shows the new date.
  const [startKey, setStartKey] = useState(0);
  const [bookingFor, setBookingFor] = useState(false);
  const t = useT();
  const [section, setSection] = useState<'progress' | 'sessions' | 'notes' | 'account'>('sessions');
  const [tagging, setTagging] = useState(false);
  const [linkPick, setLinkPick] = useState(false);
  const [linkTo, setLinkTo] = useState<{ id: string; name: string; username: string | null } | null>(null);
  const [tags, setTags] = useState<Tag[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [openSession, setOpenSession] = useState<string | null>(null);
  const [rating, setRating] = useState<SessionRow | null>(null);
  // Bumped after a rating so the past list reloads with the new tick.
  const [pastKey, setPastKey] = useState(0);
  const [levelSheet, setLevelSheet] = useState(false);
  const [aboutSheet, setAboutSheet] = useState(false);
  const [editingGoal, setEditingGoal] = useState(false);
  // «Сделать целью»: the AI's goal for the player and the coach's version.
  // Saved as is, both are kept; edited by hand, the text goes to both.
  const [suggestedGoal, setSuggestedGoal] = useState<{ player: string; coach: string } | null>(null);

  const loadProfile = () => {
    profileApi<Profile>(`/api/coach/clients/${clientId}/profile`).then(setProfile).catch(() => setProfile(null));
  };
  useEffect(loadProfile, [clientId]);
  // Back from a lesson opened from this card (CoachScreen's history): its lessons may have changed.
  useEffect(() => {
    window.addEventListener('coach:card-refresh', loadProfile);
    return () => window.removeEventListener('coach:card-refresh', loadProfile);
  }, [clientId]);

  const load = () => api<Detail>(`/api/coach/clients/${clientId}`).then((r) => {
    setD(r); setNotes(r.client.notes ?? ''); setSavedNotes(r.client.notes ?? '');
    setNickname(r.client.nickname ?? ''); setSavedNickname(r.client.nickname ?? '');
    setWorkingOn(r.client.working_on ?? '');
    setSavedWorkingOn(r.client.working_on ?? '');
  });

  useEffect(() => { load().catch(() => setD(null)); }, [clientId]);

  // The coach's own tags, for the sheet the + opens.
  useEffect(() => {
    api<Tag[]>('/api/coach/groups').then(setTags).catch(() => setTags([]));
  }, [clientId]);

  // Loading: the header straight away, from what the screen that opened it knew.
  if (!d) return (
    <div data-layer style={{ display: 'contents' }}>
      {preview?.name ? (
        <div className="detailhead profile detailhead--preview">
          <button className="detailhead__back" data-back onClick={onBack}>{backLabel ?? t('co.profile.back')}</button>
          <div className="profile__top">
            <div className="avatar" aria-hidden="true">{initials(preview.name)}</div>
            <div className="profile__id"><div className="detailhead__name">{preview.name}</div></div>
          </div>
        </div>
      ) : <button className="detailhead__back" data-back onClick={onBack} style={{ marginLeft: 0 }}>{backLabel ?? t('co.profile.back')}</button>}
      <div className="list"><div className="skeleton" style={{ height: 100 }} /><div className="skeleton" /></div>
    </div>
  );

  const realName = `${d.client.first_name} ${d.client.last_name ?? ''}`.trim();
  const livePackages = d.packages.filter((p) => p.payment_status !== 'refunded');
  const now = Date.now();
  const upcoming = d.sessions
    .filter((s) => new Date(s.starts_at).getTime() > now && s.status !== 'cancelled')
    .reverse();
  /**
   * The same three statuses the sessions endpoint lists, so the label on
   * the tab and the rows under it are counting one thing. Cancellations
   * are not sessions anybody had.
   */
  const past = d.sessions.filter((s) =>
    new Date(s.starts_at).getTime() <= now
    && (s.status === 'booked' || s.status === 'attended' || s.status === 'no_show'));
  const shown = tab === 'upcoming' ? upcoming : past;

  /**
   * Saved on a button, not on blur.
   *
   * Blur-to-save is invisible: there's no moment where you know it
   * worked, so a silent failure looks identical to success — which is
   * exactly how these notes went nowhere for weeks against a missing
   * endpoint.
   */
  async function refundPackage(p: any) {
    const unused = Number(p.balance);
    const used = Number(p.credits_granted) - unused;
    const ok = await confirm(
      t('co.cd.removePkgTitle') +
      t('co.cd.pkgUnused', { n: unused, sessions: t.word('co.today.sessions', unused) }) +
      (used > 0 ? t('co.cd.removePkgUsed', { n: used }) : '') +
      t('co.cd.removePkgMoney'));
    if (!ok) return;

    try {
      const res = await api<{ removed: number; balance: number }>(
        `/api/coach/clients/${clientId}/packages/${p.id}/refund`,
        { method: 'POST', body: JSON.stringify({}) });
      haptic.success();
      await alert(t('co.cd.creditsRemoved', { n: res.removed, credits: t.word('book.credit', res.removed), balance: res.balance }));
      load();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.cd.couldNotDo', { message: err.message }));
    }
  }

  async function switchToCredits() {
    /**
     * Nothing free: this is «возьмите кредит с другого занятия», not a
     * dead end. The soonest unpaid cash lesson opens «Заменить оплату»,
     * where a booked lesson can hand its credit over.
     */
    if (Number(d?.client.credits ?? 0) <= 0) {
      const now = Date.now();
      const cash = (d?.sessions ?? []).filter((sx) =>
        sx.payment_mode === 'cash' && !sx.amount_paid_at && sx.status !== 'cancelled');
      const next = cash.filter((sx) => new Date(sx.starts_at).getTime() > now)
        .sort((x, y) => x.starts_at.localeCompare(y.starts_at))[0]
        ?? cash.sort((x, y) => y.starts_at.localeCompare(x.starts_at))[0];
      if (!next) { await alert(t('co.cd.switchNone')); return; }
      setSwitchFor(next.booking_id);
      return;
    }
    // What it would leave uncovered ahead (N5): said, not refused.
    const pre = await api<{ costsLater?: CostsLater }>(`/api/coach/clients/${clientId}/switch-to-credits`).catch(() => null);
    const later = costsLaterText(t, pre?.costsLater);
    const ok = await confirm(
      t('co.cd.switchTitle') + t('co.cd.switchBody') + (later ? `\n\n${later}` : ''));
    if (!ok) return;
    try {
      const res = await api<{ switched: number; remaining: number; balance: number }>(
        `/api/coach/clients/${clientId}/switch-to-credits`,
        { method: 'POST', body: JSON.stringify({}) });
      haptic.success();
      await alert(
        t('co.cd.switched', {
          n: res.switched, sessions: t.word('co.today.sessions', res.switched),
          balance: res.balance, credits: t.word('book.credit', res.balance),
        }) +
        (res.remaining > 0 ? t('co.cd.switchedCash', { n: res.remaining }) : ''));
      load();
    } catch (err: any) {
      haptic.error();
      await alert(err.message === 'nothing_to_switch'
        ? t('co.cd.switchNone')
        : err.message === 'insufficient_credits'
          ? t('co.cd.noCreditsToUse', { name: d!.client.first_name })
          : t('co.cd.couldNotDo', { message: err.message }));
    }
  }

  /**
   * A standing note, not one per session. It stays until the coach
   * changes it — which is how coaching works: you focus on the backhand
   * for a month, not for a Tuesday.
   */
  async function saveWorkingOn() {
    try {
      const asSuggested = suggestedGoal && workingOn.trim() === suggestedGoal.coach.trim();
      await api(`/api/coach/clients/${clientId}/working-on`, {
        method: 'PUT',
        body: JSON.stringify(asSuggested
          ? { text: suggestedGoal!.player, coachText: suggestedGoal!.coach }
          : { text: workingOn }),
      });
      setSuggestedGoal(null);
      setSavedWorkingOn(workingOn);
      setEditingGoal(false);
      loadProfile();
      haptic.success();
      await alert(workingOn.trim()
        ? t('co.cd.workingOnSaved')
        : t('co.cd.cleared'));
    } catch (err: any) {
      haptic.error();
      await alert(t('co.cd.couldNotSave', { message: err.message }));
    }
  }

  async function toggleBlock() {
    const blocking = !d!.client.blocked;
    const ok = await confirm(blocking
      ? t('co.cd.blockTitle')
      : t('co.cd.unblockTitle'));
    if (!ok) return;
    try {
      const res = await api<{ cancelled: number }>(`/api/coach/clients/${clientId}/block`, {
        method: 'PUT', body: JSON.stringify({ blocked: blocking }),
      });
      haptic.success();
      if (blocking && res.cancelled > 0) await alert(t('co.cd.blockedDone', {
        n: res.cancelled, sessions: t.word('co.today.sessions', res.cancelled),
      }));
      load();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.cd.couldNot', { message: err.message }));
    }
  }

  async function topUp(amount: number, method: PayMethod) {
    if (!Number.isFinite(amount) || amount <= 0) { await alert(t('co.cd.topUpAmount')); return; }
    try {
      const res = await api<{ credits: number; rate: number; balance: number }>(
        `/api/coach/clients/${clientId}/topup`,
        { method: 'POST', body: JSON.stringify({ amount, method }) });
      rememberMethod(method);
      setToppingUp(false);
      haptic.success();
      await alert(t('co.cd.topUpDone', { amount, rate: res.rate, n: res.credits, credits: t.word('book.credit', res.credits), balance: res.balance }));
      load();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.cd.couldNot', { message: err.message }));
    }
  }

  async function saveNickname() {
    try {
      await api(`/api/coach/clients/${clientId}/nickname`, {
        method: 'PUT', body: JSON.stringify({ nickname }),
      });
      setSavedNickname(nickname);
      setEditingName(false);
      haptic.success();
      await load();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.cd.couldNotSave', { message: err.message }));
    }
  }

  async function saveNotes() {
    try {
      await api(`/api/coach/clients/${clientId}/notes`, {
        method: 'PUT', body: JSON.stringify({ notes }),
      });
      setSavedNotes(notes);
      haptic.success();
      await alert(t('co.cd.saved'));
    } catch (err: any) {
      haptic.error();
      await alert(t('co.cd.couldNotSave', { message: err.message }));
    }
  }



  if (openSession) {
    return <SessionView bookingId={openSession} onBack={() => { setOpenSession(null); loadProfile(); }} />;
  }
  if (payHistory) {
    return <PaymentHistory clientId={clientId} name={d.client.nickname ?? d.client.first_name}
      onBack={() => { setPayHistory(false); load(); }} />;
  }

  const goalSince = profile?.workingOn?.since
    ? new Intl.DateTimeFormat(localeOf(getLang()), { day: 'numeric', month: 'short' }).format(new Date(profile.workingOn.since)).toUpperCase()
    : null;

  return (
    // A layer over the Clients tab: Telegram's back arrow presses «‹ Клиенты» (lib/nav.ts).
    <div data-layer style={{ display: 'contents' }}>
      <div className="detailhead profile">
        <button className="detailhead__back" data-back onClick={onBack}>{backLabel ?? t('co.profile.back')}</button>
        {editingName ? (
          <>
            <input className="paybar__opt"
              style={{ width: '100%', textAlign: 'left' }}
              autoFocus
              placeholder={realName}
              value={nickname}
              onChange={(e) => setNickname(e.target.value)} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="savebtn" style={{ width: 'auto', flex: 1 }}
                onClick={saveNickname}>{t('co.profile.save')}</button>
              {/* Nothing left unsaved after a cancel either (lib/nav.ts). */}
              <button className="paybar__opt" data-save style={{ marginTop: 10 }}
                onClick={() => { setNickname(savedNickname); setEditingName(false); }}>
                {t('co.profile.cancelEdit')}
              </button>
            </div>
            <div className="detailhead__meta" style={{ marginTop: 6 }}>
              {t('co.profile.nicknameHint', { name: realName })}
            </div>
          </>
        ) : (
          <div className="profile__top">
            <div className="avatar" aria-hidden="true">
              {initials(d.client.nickname || realName)}
              {d.client.is_regular && <span className="avatar__star" title={t('co.reg.label')}>★</span>}
            </div>
            <div className="profile__id">
              <div className="detailhead__name">
                {d.client.nickname ? d.client.nickname : realName}
                <button className="profile__edit" aria-label={t('co.profile.editName')}
                  onClick={() => {
                    setNickname(d.client.nickname ?? realName);
                    setEditingName(true);
                  }}>
                  ✎
                </button>
              </div>
              {d.client.nickname && d.client.nickname !== realName && (
                <div className="detailhead__meta">{realName}</div>
              )}
              <div className="profile__since">
                {d.totals.first_session
                  ? t('co.profile.since', { day: fmtLongDay(new Date(d.totals.first_session)) })
                  : t('co.profile.never')}
                {d.totals.last_session && t('co.profile.lastSession', { ago: ago(d.totals.last_session) })}
              </div>
              {/* Side and hand are padel's: not for a tennis player. */}
              {profile && profile.sport?.client !== 'tennis' && <AboutChips about={profile.about} onEdit={() => setAboutSheet(true)} />}
            </div>
          </div>
        )}
        <div className="profile__chips">
          {d.client.offline ? (
            <>
              <span className="pchip pchip--warn">{t('co.off.tag')}</span>
              {d.client.added_manually_at && (
                <span className="pchip">{t('co.off.addedOn', { day: fmtShortDay(new Date(d.client.added_manually_at)) })}</span>
              )}
            </>
          ) : (
            <span className="pchip">{d.client.username ? `@${d.client.username}` : t('co.profile.noUsername')}</span>
          )}
          {d.client.phone && <a className="pchip pchip--link" href={`tel:${d.client.phone}`}>{d.client.phone}</a>}
          {!d.client.bot_started && !d.client.offline && <span className="pchip pchip--warn">{t('co.profile.noMessages')}</span>}
        </div>
        {d.client.offline && (
          <button className="off-linkban" onClick={() => { haptic.tap(); setLinkPick(true); }}>
            <span>🔗 {t('co.off.linkAsk')}</span><b>{t('co.off.linkGo')}</b>
          </button>
        )}

        {/* The tags, and the same facts their card in the list shows.
            With no tags yet there is nothing to tap, so the profile — and
            only the profile — offers the way in. */}
        <div className="clientcard__tags" style={{ marginTop: 10 }}>
          {(d.groups ?? []).map((g) => (
            <button key={g.id} className="tagpill"
              style={g.color ? { color: g.color, background: `${g.color}1f` } : undefined}
              onClick={() => { haptic.tap(); setTagging(true); }}
              aria-label={t('co.tag.addTo', { name: d.client.first_name })}>
              {/* A tag is the coach's own word: never translated. */}
              {g.name}
            </button>
          ))}
          {(d.groups ?? []).length === 0 && (
            <button className="tagadd" onClick={() => { haptic.tap(); setTagging(true); }}
              aria-label={t('co.tag.addTo', { name: d.client.first_name })}>
              {t('co.tag.add')}
            </button>
          )}
        </div>

        <ProfileFacts rhythm={d.rhythm} />

        {/* Three things a coach does from here, side by side. */}
        <div className="profile__actions">
          <button className="pact pact--primary" onClick={() => { haptic.tap(); setBookingFor(true); }}>
            {t('co.profile.book')}
          </button>
          <button className="pact pact--credit" onClick={() => { haptic.tap(); setToppingUp(true); }}>
            {t('co.profile.addCredit')}
          </button>
          <button className="pact pact--message" disabled={!d.client.username}
            onClick={() => { haptic.tap(); openExternal(`https://t.me/${d.client.username}`); }}>
            {t('co.profile.message')}
          </button>
        </div>
      </div>


      <div className="list pp-top">
        {profile && profile.sport?.client !== 'tennis' && (
          <LevelCard level={profile.level} playtomic={profile.about.playtomicLevel}
            name={d.client.first_name} onSet={() => setLevelSheet(true)} />
        )}
        <div className="pstats pp-stats">
          <div><b>{profile?.stats.sessions ?? d.totals.taken}</b><span>{t('co.profile.sessions')}</span></div>
          <div className={profile?.stats.turnedUp != null && profile.stats.turnedUp < 85 ? 'pstats--bad' : 'pstats--money'}>
            <b>{profile?.stats.turnedUp != null ? `${profile.stats.turnedUp}%` : '–'}</b><span>{t('co.profile.turnedUp')}</span>
          </div>
          <div><b>{d.client.credits}</b><span>{t('co.profile.credits')}</span></div>
        </div>
        {d.coverage && <CoverageLine c={d.coverage} />}
        <RegularCard clientId={clientId} name={d.client.first_name}
          regular={d.client.is_regular === true} onChanged={load} />
      </div>

      {/* Outside .pp-top so it can stay pinned for the whole profile:
          a sticky element only sticks within its own parent. */}
      <div className="ptabs" role="tablist">
        {([['sessions', t('co.profile.tabSessions')], ['progress', t('co.profile.tabProgress')],
           ['notes', t('co.profile.tabNotes')], ['account', t('co.profile.tabSettings')]] as const).map(([id, label]) => (
          <button key={id} role="tab" className="ptab" aria-selected={section === id}
            onClick={() => { haptic.tap(); setSection(id); }}><span>{label}</span></button>
        ))}
      </div>

      {section === 'progress' && (
        <div className="list">
          <div className={`card pp-goal${editingGoal || !savedWorkingOn ? ' pp-goal--edit' : ''}`}>
            <div className="pp-goal__label">
              <span>{t('co.cd.goalTitle')}{goalSince ? t('co.cd.goalSince', { day: goalSince }) : ''}</span>
              <Vis who="player" />
            </div>
            {editingGoal || !savedWorkingOn ? (
              <>
                <GrowArea className="notearea" rows={2}
                  placeholder={t('co.cd.goalPlaceholder')}
                  value={workingOn}
                  onChange={(e) => setWorkingOn(e.target.value)} />
                <div className="card__when">{t('co.cd.goalMessage', { name: d.client.first_name })}</div>
                {workingOn !== savedWorkingOn ? (
                  <button className="savebtn" onClick={saveWorkingOn}>
                    {workingOn.trim() ? t('co.cd.goalSave') : t('co.cd.goalClear')}
                  </button>
                ) : savedWorkingOn && (
                  <button className="paybar__opt" onClick={() => setEditingGoal(false)}>{t('co.cd.goalDone')}</button>
                )}
              </>
            ) : (
              <>
                <div className="pp-goal__text">{savedWorkingOn}</div>
                {profile?.workingOn && (
                  <div className="card__when">
                    {t('co.cd.goalSessions', {
                      n: profile.workingOn.sessionsSince,
                      sessions: t.word('co.today.sessions', profile.workingOn.sessionsSince),
                    })}
                  </div>
                )}
                <div className="pp-row2">
                  <button className="paybar__opt" onClick={() => { haptic.tap(); setEditingGoal(true); }}>{t('co.cd.goalChange')}</button>
                  <button className="paybar__opt" onClick={() => { setWorkingOn(''); setEditingGoal(true); }}>{t('co.cd.goalFinished')}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {section === 'progress' && (
        <>
          {/* One chart per sport for a coach who teaches both; as before otherwise. */}
          {(profile?.sport?.coach.includes('tennis') ? (['padel', 'tennis'] as const) : [undefined]).map((sp) => (
            <ProgressChart key={sp ?? 'all'} clientId={clientId} titled={false} sport={sp}
              heading={sp ? t(sp === 'padel' ? 'co.sport.padel' : 'co.sport.tennis') : undefined} />
          ))}
          <div style={{ padding: '0 16px' }}>
            <StartingScoresCard key={startKey} clientId={clientId} onOpen={() => setBaseline(true)} />
          </div>
          <div className="list">
            {profile?.level && profile.sport?.client !== 'tennis' && <LevelTrend history={profile.level.history} />}
            <MatchList clientId={clientId} name={d.client.first_name} />
          </div>
        </>
      )}

      {section === 'sessions' && (
        <>
          {/* The filter and what is owed share one line: the list below is
              what this tab is for, so nothing above it takes a row of its own. */}
          <div className="pfilter">
            <div className="pfilter__tabs" role="tablist">
              <button role="tab" className="pfilter__pill" aria-selected={tab === 'upcoming'}
                onClick={() => { haptic.tap(); setTab('upcoming'); }}>
                {t('co.profile.upcoming')} {upcoming.length}
              </button>
              <button role="tab" className="pfilter__pill" aria-selected={tab === 'past'}
                onClick={() => { haptic.tap(); setTab('past'); }}>
                {t('co.profile.past')} {past.length}
              </button>
            </div>
            {d.totals.owed > 0 && (
              <span className="pfilter__owed">{t('co.profile.unpaid', { amount: d.totals.owed })}</span>
            )}
          </div>

          {tab === 'past' ? (
            <SessionsTab key={pastKey} clientId={clientId} name={d.client.nickname ?? d.client.first_name}
              onOpen={onOpenLesson ?? setOpenSession} onRate={setRating} onPaid={load} />
          ) : upcoming.length === 0 ? (
            <div className="empty" style={{ padding: 28 }}>{t('co.profile.nothingAhead')}</div>
          ) : (
            <div className="list" style={{ paddingBottom: 0 }}>
              {byMonth(upcoming, (sx) => sx.starts_at).map(([month, rows], i) => (
                <div key={month}>
                  <div className="srows__month">{month}</div>
                  {i === 0 && <SessionHead last={t('co.tbl.payment')} menu />}
                  <div className="srows srows--card">
              {rows.map((sx) => (
                <SessionLine key={sx.booking_id}
                  starts={sx.starts_at}
                  title={sx.title}
                  kind={sx.kind}
                  people={sx.people}
                  series={Boolean(sx.series_id)}
                  end={payCell(t, sx) ?? (sx.payment_mode === 'cash'
                    ? (sx.amount_paid_at
                      ? t('co.profile.paid', { amount: sx.amount_due ?? 0 })
                      : t('co.profile.owedOne', { amount: Math.max(0, Number(sx.amount_due ?? 0) - Number(sx.received ?? 0)) }))
                      // Partly in credits: «+ 0,25 кр.».
                      + (Number(sx.credits_charged ?? 0) > 0
                        ? t('co.profile.plusCredits', { n: String(Math.round(Number(sx.credits_charged) * 100) / 100).replace('.', getLang() === 'ru' ? ',' : '.') })
                        : '')
                    : t('co.profile.creditsUsed', {
                        n: sx.credits_charged ?? 1,
                        credits: t.word('book.credit', Number(sx.credits_charged ?? 1)),
                      }))}
                  menu={sx.status === 'booked' ? (
                    <RowMenu items={[
                      { label: t('co.profile.move'), onSelect: () => setMoving(sx) },
                      // Paid ahead in money: changed or undone like any payment.
                      ...(sx.payment_mode === 'cash' && (sx.amount_paid_at || Number(sx.received ?? 0) > 0)
                        ? [{ label: t('lp.edit'), onSelect: () => setEditPay(sx.booking_id) }] : []),
                      { label: t('co.profile.cancel'), danger: true, onSelect: () => setCancelOne(sx) },
                    ]} label={t('co.profile.move')} />
                  ) : undefined} />
              ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'upcoming' && upcoming.length > 0 && (
            <div style={{ padding: '4px 16px 6px', textAlign: 'right' }}>
              <button className="quietlink" onClick={() => setCancelAll(true)}>
                {t('co.profile.cancelAllUpcoming', { n: upcoming.length })}
              </button>
            </div>
          )}

          {livePackages.length > 0 && (
            <>
              <div className="section-title">{t('co.profile.packages')}</div>
              <div className="list">
                {livePackages.map((pk) => {
                  const left = Number(pk.balance);
                  const total = Number(pk.credits_granted);
                  const daysLeft = pk.expires_at
                    ? Math.ceil((new Date(pk.expires_at).getTime() - Date.now()) / 86400000)
                    : null;
                  return (
                    <div className="pkgcard" key={pk.id}>
                      <div className="pkgcard__head">
                        <div style={{ minWidth: 0 }}>
                          {/* The package's name is the coach's own wording. */}
                          <div className="pkgcard__name">
                            {pk.package_name ?? t('co.pkg.sessions', {
                              n: total, sessions: t.word('co.today.sessions', total),
                            })}
                          </div>
                          <div className="card__when">
                            {daysLeft === null ? t('co.profile.pkgNoExpiry')
                              : daysLeft <= 0 ? t('co.profile.pkgExpired')
                              : t('co.profile.pkgExpires', { n: daysLeft, days: t.word('co.days', daysLeft) })}
                            {' · '}
                            {t('co.profile.pkgBought', {
                              day: fmtShortDay(new Date(pk.purchased_at)), amount: pk.amount_paid,
                            })}
                          </div>
                        </div>
                        {left > 0 && (
                          <RowMenu label={t('co.profile.pkgMore')} items={[
                            { label: t('co.profile.pkgRemove'), danger: true, onSelect: () => refundPackage(pk) },
                          ]} />
                        )}
                      </div>
                      <div className="pkgcard__bar">
                        <span style={{ width: `${total > 0 ? Math.round((left / total) * 100) : 0}%` }} />
                      </div>
                      <div className="pkgcard__left">{t('co.profile.pkgLeft', { left, total })}</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div className="list">
            <button className="settingsrow" onClick={() => { haptic.tap(); setPayHistory(true); }}>
              <span className="settingsrow__text"><span>{t('lp.open')}</span></span>
              <span className="settingsrow__chev" aria-hidden="true">›</span>
            </button>
            <button className="savebtn savebtn--big" onClick={() => onSell(d.client)}>
              {t('co.profile.sellPackage')}
            </button>
          </div>
        </>
      )}

      {section === 'notes' && (
        <div className="list pp-notes">
          {/*
            Above everything on this tab, because this is where the coach
            is about to type and what the summary reads. A reminder
            anywhere else is one they would have to go and look for.
          */}
          {d.client.health_consent === 'no' && (
            <div className="warnline">
              {t('co.cd.noHealthNotes')}
            </div>
          )}
          <CoachJournal clientId={clientId} name={d.client.nickname || d.client.first_name} onEdit={onOpenLesson ?? setOpenSession} />
          <AiNotes clientId={clientId} name={d.client.first_name}
            onUseFocus={(player, coach) => { setSuggestedGoal({ player, coach }); setWorkingOn(coach); setEditingGoal(true); setSection('progress'); window.scrollTo(0, 0); }} />
          <div className="card notecard">
            <div className="notecard__head">
              <span className="notecard__icon">🔒</span>
              <span>{t('co.cd.standingNotes')}</span>
              <Vis who="me" />
            </div>
            <GrowArea className="notearea" rows={3}
              placeholder={t('co.cd.notesPlaceholder')}
              value={notes} onChange={(e) => setNotes(e.target.value)} />
            {notes !== savedNotes && (
              <button className="savebtn" onClick={saveNotes}>{t('co.today.saveNote')}</button>
            )}
          </div>
        </div>
      )}

      {section === 'account' && (
        <>
          {/* «Вид спорта»: only for a coach who teaches padel and tennis. */}
          {profile?.sport && profile.sport.coach.includes('tennis') && (
            <ClientSport clientId={clientId} value={profile.sport.client} onChanged={loadProfile} />
          )}
          <div className="section-title">{t('co.profile.settingsBooking')}</div>
          <div className="list">
            <div className="card">
              <label className="checkline">
                <input type="checkbox" defaultChecked={d.client.half_hour_starts === true}
                  onChange={async (e) => {
                    await api(`/api/coach/clients/${clientId}/half-hour`, {
                      method: 'PUT', body: JSON.stringify({ allowed: e.target.checked }),
                    });
                    haptic.success();
                  }} />
                <span>
                  {t('co.profile.halfHour')}
                  <small>{t('co.profile.halfHourHint')}</small>
                </span>
              </label>
            </div>

            {/**
              * Never hidden at a balance of 0. A client whose credits are
              * all sitting on booked lessons still has credits — and the
              * coach's next question is which lesson gives one up, not
              * whether the button exists.
              */}
            <button className="paybar__opt" style={{ width: '100%' }} onClick={switchToCredits}>
              {t('co.profile.payWithCredits')}
            </button>
          </div>

          <BotLanguageCard clientId={clientId} value={d.client.bot_language ?? null} />

          <PayerSection clientId={clientId} name={d.client.first_name}
            currentPayer={d.client.pays_for_by ?? null} onChanged={load} />

          <RatesSection clientId={clientId} name={d.client.first_name} />

          <div className="section-title">{t('co.profile.access')}</div>
          <div className="list">
            {d.client.blocked && (
              <div className="warnline">{t('co.profile.blocked')}</div>
            )}
            <button className="settingsrow" onClick={toggleBlock}>
              <span className="settingsrow__text">
                <span className={d.client.blocked ? undefined : 'settingsrow__danger'}>
                  {d.client.blocked ? t('co.profile.unblock') : t('co.profile.block')}
                </span>
                <small>{t('co.profile.blockHint')}</small>
              </span>
              <span className="settingsrow__chev" aria-hidden="true">›</span>
            </button>
          </div>

          <div className="card__when" style={{ padding: '4px 18px 0', lineHeight: 1.45 }}>
            {t('co.profile.privateNote')}
          </div>
        </>
      )}

      {toppingUp && <TopUpSheet onClose={() => setToppingUp(false)} onSave={topUp} />}
      {editPay && (
        <EditPaymentSheet bookingId={editPay} name={d.client.nickname ?? d.client.first_name}
          onClose={() => setEditPay(null)} onDone={load} />
      )}
      {switchFor && (
        <PaySwitchSheet bookingId={switchFor}
          onClose={() => setSwitchFor(null)}
          onDone={() => { setSwitchFor(null); load(); }} />
      )}

      {baseline && (
        <BaselineSheet clientId={clientId} name={d.client.first_name} coachSports={profile?.sport?.coach}
          onClose={() => { setBaseline(false); load(); setStartKey((k) => k + 1); }} />
      )}
      {levelSheet && (
        <LevelSheet clientId={clientId} name={d.client.first_name}
          current={profile?.level?.current ?? null}
          onClose={() => setLevelSheet(false)} onSaved={loadProfile} />
      )}
      {aboutSheet && profile && (
        <AboutSheet clientId={clientId} name={d.client.first_name} about={profile.about}
          onClose={() => setAboutSheet(false)} onSaved={loadProfile} />
      )}

      {bookingFor && (
        <BookForSheet clientId={clientId} name={d.client.first_name}
          onClose={() => setBookingFor(false)} onDone={load} />
      )}

      {linkPick && (
        <LinkPickSheet offline={{ id: clientId, name: d.client.nickname ?? d.client.first_name }}
          onClose={() => setLinkPick(false)}
          onPick={(c) => { setLinkPick(false); setLinkTo(c); }} />
      )}
      {linkTo && (
        <LinkConfirmSheet offline={{ id: clientId, name: d.client.nickname ?? d.client.first_name }} into={linkTo}
          onClose={() => setLinkTo(null)}
          onLinked={async (res) => {
            const name = d.client.nickname ?? d.client.first_name;
            setLinkTo(null);
            await alert(res.upcoming > 0 ? t('co.off.doneUpcoming', { name, n: res.upcoming }) : t('co.off.done', { name }));
            // The manual profile is gone: show the one everything moved to.
            if (onOpenClient) onOpenClient(res.into); else onBack();
          }} />
      )}

      {tagging && (
        <TagSheet
          client={{ id: clientId, name: d.client.nickname ?? d.client.first_name, groups: d.groups ?? [] }}
          tags={tags}
          onClose={() => setTagging(false)}
          onChanged={() => {
            load();
            api<Tag[]>('/api/coach/groups').then(setTags).catch(() => {});
          }} />
      )}

      {/* A pair or group: one notes sheet for the whole session, on this player's tab. */}
      {rating && rating.people > 1 && rating.session_id && (
        <GroupNotesSheet sessionId={rating.session_id} startTab={rating.booking_id}
          onClose={() => setRating(null)}
          onDone={() => { setPastKey((k) => k + 1); loadProfile(); }} />
      )}
      {rating && !(rating.people > 1 && rating.session_id) && (
        <RatingSheet booking={{ bookingId: rating.booking_id, clientId, name: d.client.first_name }}
          onClose={() => setRating(null)}
          onDone={() => { setPastKey((k) => k + 1); loadProfile(); }} />
      )}

      {moving && (
        <MoveSheet booking={moving} name={d.client.first_name}
          isSeries={Boolean(moving.series_id)}
          onClose={() => setMoving(null)} onDone={load} />
      )}

      {cancelOne_ && (
        <CancelSheet
          title={t('co.profile.cancelTitle')}
          subtitle={t('co.profile.cancelBody', {
            day: fmtLongDay(new Date(cancelOne_.starts_at)), time: fmtTime(cancelOne_.starts_at),
          })}
          warning={!d.client.bot_started ? t('co.today.cancelNoMessage') : undefined}
          confirmLabel={t('co.today.cancelDo')}
          onClose={() => setCancelOne(null)}
          onConfirm={async (reason) => {
            await api(`/api/coach/bookings/${cancelOne_.booking_id}/cancel`, {
              method: 'POST', body: JSON.stringify({ reason }),
            });
            setCancelOne(null);
            load();
          }}
        />
      )}

      {cancelAll && (
        <CancelSheet
          title={t('co.profile.cancelAllTitle', { n: upcoming.length })}
          subtitle={t('co.profile.cancelAllBody')}
          warning={!d.client.bot_started ? t('co.today.cancelNoMessage') : undefined}
          confirmLabel={t('co.profile.cancelAllConfirm', { n: upcoming.length })}
          onClose={() => setCancelAll(false)}
          onConfirm={async (reason) => {
            const res = await api<{ cancelled: number; refunded: number }>(
              `/api/coach/clients/${clientId}/cancel-upcoming`,
              { method: 'POST', body: JSON.stringify({ reason }) });
            setCancelAll(false);
            await alert(t('co.profile.cancelDone', {
              n: res.cancelled, r: res.refunded, credits: t.word('book.credit', res.refunded),
            }));
            load();
          }}
        />
      )}
    </div>
  );
}
